// src/hooks/useNotifications.js
import React from "react";
import { createContext, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import consumer from "../../channels/consumer";
import { getNotifications, notificationCsvDownload, notificationSetting, readAllNotification, readNotification, toggleEmailNotification, togglePushNotification } from "../services/notification";
const NotificationsContext = createContext(null);

export const NotificationsProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [notificationsSetting, setNotificationsSetting] = useState([]);
  const [notificationPreferences, setNotificationPreferences] = useState({});

  const fetchNotificationsSetting = async () => {
    try {
      const response = await notificationSetting();
      setNotificationsSetting(response)
      // convert API data to preferences object
      const prefs = {};
      response.forEach((item) => {
        prefs[item.action_type] = {
          email: item.email_enabled,
          push: item.push_enabled
        };
      });

      setNotificationPreferences(prefs);
    }
    catch (error) {
      console.error(error);
    }
  }

  const fetchNotifications = async () => {
    try {
      const data = await getNotifications(); // array

      const mappedNotifications = data.map(item => ({
        user_id: item.user_id,
        id: item.id,
        type: item.action_type,
        priority: item.priority,
        ticket_id: item.id, // item.action_value // string "8322"
        title: item.title || `Ticket #${item.action_value}`,
        message: item.body,
        bookmarked: item.bookmarked,
        created_at: item.created_at,
        description: item.description,
        labels: item.labels || [],
        isRead: item.read,
        read_at: item.read_at,
        updated_at: item.updated_at,
        out_of_stock_at: item.out_of_stock_at,
        payload: item.payload,
      }));

      setNotifications(mappedNotifications);
    } catch (err) {
      console.error("Failed to load notifications", err);
    }
  };

  const handleCsvDownload = async (id) => {
    if (!id) return;

    try {
      const response = await notificationCsvDownload(id);

      // If API returns raw CSV string
      const blob = new Blob([response], { type: "text/csv;charset=utf-8;" });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "notification_data.csv"); // file name
      document.body.appendChild(link);

      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

    } catch (err) {
      console.error("Failed to download CSV", err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const subscription = consumer.subscriptions.create(
      { channel: "NotificationChannel" },
      {
        received: (data) => {
          setNotifications(prev => {
            //  prevent duplicates
            if (prev.some(n => n.id === data.id)) return prev;

            return [{
              user_id: data.user_id,
              id: data.id,
              type: data.action_type,
              priority: data.priority,
              ticket_id: data.id,
              title: data.title,
              message: data.body,
              bookmarked: data.bookmarked,
              created_at: data.created_at,
              description: data.description,
              labels: data.labels || [],
              isRead: data.read,
              read_at: data.read_at,
              updated_at: data.updated_at,
              out_of_stock_at: data.out_of_stock_at,
              payload: data.payload,
            }, ...prev];
          });
        }
      }
    );

    return () => subscription.unsubscribe();
  }, []);

  const markAsRead = (id) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, isRead: true } : n)
    );
  };


  const markAllAsRead = async () => {
    try {
      const response = await readAllNotification();

      if (response?.success) {
        setNotifications(prev =>
          prev.map(n => ({
            ...n,
            isRead: true,
            read_at: n.read_at || new Date().toISOString(),
          }))
        );
      }
    } catch (error) {
      console.error("Failed to mark all notifications as read", error);
    }
  };

  const markNotificationAsRead = async (id) => {
    try {
      await readNotification(id);

      setNotifications(prev =>
        prev.map(n =>
          n.id === id
            ? { ...n, isRead: true, read_at: new Date().toISOString() }
            : n
        )
      );
    } catch (error) {
      console.error("Failed to mark notification as read", error);
    }
  };

const getTypeConfig = (priority = "low", type) => {

  //  Growth types should always be green
  if (["sales_growth", "hero_product_alert"].includes(type)) {
    return {
      color: "#22c55e",
      bg: "#ecfdf5",
      icon: "bi-graph-up-arrow",
      gradient: "linear-gradient(135deg,#22c55e,#16a34a)",
      // shadowColor: "rgba(34, 197, 94, 0.3)",
    };
  }

  const map = {
    success: {
      color: "#10b981",
      bg: "#ecfdf5",
      icon: "bi-check-circle-fill",
      gradient: "linear-gradient(135deg,#10b981,#059669)",
      shadowColor: "rgba(16, 185, 129, 0.3)",
    },
    medium: {
      color: "#f59e0b",
      bg: "#fffbeb",
      icon: "bi-exclamation-triangle-fill",
      gradient: "linear-gradient(135deg,#f59e0b,#d97706)",
      shadowColor: "rgba(245, 158, 11, 0.3)",
    },
    high: {
      color: "#ef4444",
      bg: "#fef2f2",
      icon: "bi-x-circle-fill",
      gradient: "linear-gradient(135deg,#ef4444,#dc2626)",
      shadowColor: "rgba(239, 68, 68, 0.3)",
    },
    low: {
      color: "#3b82f6",
      bg: "#eff6ff",
      icon: "bi-info-circle-fill",
      gradient: "linear-gradient(135deg,#3b82f6,#2563eb)",
      shadowColor: "rgba(59, 130, 246, 0.3)",
    },
  };

  return map[priority] || map.low;
};

  const getPriorityStyle = (priority,type) => {
    const styles = {
      High: { bg: '#fef2f2', color: '#dc2626', icon: 'bi-exclamation-circle-fill' },
      Medium: { bg: '#fff7ed', color: '#ea580c', icon: 'bi-dash-circle-fill' },
      Low: { bg: '#f0fdf4', color: '#16a34a', icon: 'bi-check-circle-fill' },
    };
    if (["sales_growth", "hero_product_alert"].includes(type)) {
    return {
      icon: "bi-graph-up-arrow",
      gradient: "linear-gradient(135deg,#22c55e,#16a34a)"
    };
  }
    return styles[priority] || styles.Medium;
  };


  useEffect(() => {
    if (!notifications.length) return;

    const prefs = notifications.reduce((acc, notification) => {
      acc[notification.id] = {
        email: notification.email_allowed ?? false,
        push: notification.inapp_allowed ?? false
      };
      return acc;
    }, {});

    setNotificationPreferences(prefs);

  }, [notifications]);

  const toggleEmail = async (actionType, currentValue, e) => {
    e.stopPropagation();

    const newValue = !currentValue;

    try {
      await toggleEmailNotification({
        action_type: actionType,
        email_enabled: newValue
      });

      setNotificationPreferences(prev => ({
        ...prev,
        [actionType]: {
          ...prev[actionType],
          email: newValue
        }
      }));
      toast.success(
        newValue
          ? "Email notification enabled"
          : "Email notification disabled"
        , {
          position: "bottom-right",
        });

    } catch (error) {
      console.error(error);
    }
  };


  const togglePush = async (actionType, currentValue, e) => {
    e.stopPropagation();

    const newValue = !currentValue;

    try {
      await togglePushNotification({
        action_type: actionType,
        toggle_inapp: newValue
      });

      setNotificationPreferences(prev => ({
        ...prev,
        [actionType]: {
          ...prev[actionType],
          push: newValue
        }
      }));
      toast.success(
        newValue
          ? "Push notification enabled"
          : "Push notification disabled"
        , {
          position: "bottom-right",
        });


    } catch (error) {
      console.error(error);
    }
  };
  return (
    <NotificationsContext.Provider
      value={{
        notifications,
        markAsRead,
        markAllAsRead,
        markNotificationAsRead,
        setNotifications,
        getTypeConfig,
        getPriorityStyle,
        handleCsvDownload,
        toggleEmail,
        togglePush,
        notificationPreferences,
        setNotificationPreferences,
        notificationsSetting,
        fetchNotificationsSetting
      }}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
export const useNotifications = () => {
  return useContext(NotificationsContext);
};